'use client';

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Calendar, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { institutionListSearchShellClass } from './InstitutionStyledSearchInput';

function TemporalLeadIcon({ Icon, dense }: { Icon: LucideIcon; dense?: boolean }) {
  const I = Icon;
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg border',
        dense ? 'h-7 w-7' : 'h-9 w-9',
        'border-primary-200 bg-primary-50 text-primary-600 dark:border-primary-800/60 dark:bg-primary-950/50 dark:text-primary-300',
      )}
    >
      <I className={cn('shrink-0 opacity-90', dense ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
    </span>
  );
}

function innerClass(dense?: boolean) {
  return cn(
    'min-w-0 flex-1 border-0 bg-transparent p-0 font-medium leading-snug text-primary-900 shadow-none ring-0 outline-none',
    dense ? 'text-xs' : 'text-sm',
    '[color-scheme:light] dark:[color-scheme:dark]',
    '[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100',
    'focus:ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50',
  );
}

export type InstitutionStyledTemporalVariant = 'date' | 'time' | 'datetime-local';

/** Extrait `AAAA-MM-JJ` depuis une chaîne API (ISO, etc.) pour `type="date"`. */
export function parseInstitutionStoredDateToInputValue(raw: string | null | undefined): string {
  if (raw == null) return '';
  const t = String(raw).trim();
  if (!t) return '';
  const m = t.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

export type InstitutionStyledTemporalInputProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'type' | 'size'
> & {
  variant: InstitutionStyledTemporalVariant;
  /** Variante compacte (cartes, grilles denses) */
  dense?: boolean;
  wrapperClassName?: string;
};

/**
 * Champs natifs date / heure / datetime-local avec le même habillage que
 * {@link InstitutionStyledSearchInput} et {@link InstitutionStyledSelect}.
 */
export const InstitutionStyledTemporalInput = forwardRef<HTMLInputElement, InstitutionStyledTemporalInputProps>(
  function InstitutionStyledTemporalInput(
    { variant, className, wrapperClassName, dense, disabled, ...rest },
    ref,
  ) {
    const Icon: LucideIcon = variant === 'time' ? Clock : Calendar;
    return (
      <div
        className={cn(
          institutionListSearchShellClass,
          dense && '!min-h-0 gap-2 px-2 py-1.5 text-xs',
          wrapperClassName,
          disabled && 'opacity-60',
        )}
      >
        <TemporalLeadIcon Icon={Icon} dense={dense} />
        <input
          ref={ref}
          type={variant}
          disabled={disabled}
          className={cn(innerClass(dense), className)}
          {...rest}
        />
      </div>
    );
  },
);

InstitutionStyledTemporalInput.displayName = 'InstitutionStyledTemporalInput';
